// Styled primitives + react-select styling for the Seed Fridge Inventory page
// and its modal. Centralized so SeedFridgeInventory.js and SlotEditorModal.js
// can share them without duplicating definitions.

import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import theme from './theme';

export const Page = styled('div')`
  min-height: calc(100vh - 50px);
  background: ${theme.color.bg};
  color: ${theme.color.text1};
  font-family: ${theme.font.body};
  padding: ${theme.space.xl} ${theme.space.xxl} 96px;
  box-sizing: border-box;
  /* Barely-there accent wash so the page doesn't feel like flat paper. */
  background-image: radial-gradient(
    circle at 20% 0%,
    rgba(16, 185, 129, 0.05) 0%,
    transparent 40%
  );
  animation: fadeIn 320ms ease-out both;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    *,
    *::before,
    *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }
`;

export const HeaderBar = styled('div')`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${theme.space.xl};
  gap: ${theme.space.md};
`;

export const BackLink = styled(Link)`
  color: ${theme.color.text3};
  font-family: ${theme.font.mono};
  font-size: 12px;
  letter-spacing: 0.04em;
  text-decoration: none;
  &:hover {
    color: ${theme.color.accent};
  }
`;

export const Eyebrow = styled('div')`
  color: ${theme.color.text4};
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
  font-family: ${theme.font.mono};
  margin-bottom: ${theme.space.xs};
`;

export const Title = styled('h1')`
  font-family: ${theme.font.display};
  font-weight: 600;
  font-size: 28px;
  line-height: 1.15;
  margin: 0;
  color: ${theme.color.text0};
`;

export const Subtitle = styled('div')`
  color: ${theme.color.text3};
  font-size: 13px;
  margin-top: ${theme.space.xs};
`;

export const Layout = styled('div')`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: ${theme.space.xl};
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled('section')`
  background: ${theme.color.bgRaised};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  padding: ${theme.space.lg};
  box-shadow: ${theme.shadow.card};
  animation: cardIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: ${(p) => p.delay || 0}ms;
  @keyframes cardIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const CardLabel = styled('h2')`
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 11px;
  font-family: ${theme.font.mono};
  color: ${theme.color.text3};
  margin: 0 0 ${theme.space.md};
  font-weight: 500;
`;

export const FieldLabel = styled('label')`
  display: block;
  font-family: ${theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.color.text3};
  margin: ${theme.space.md} 0 ${theme.space.xs};
`;

export const ModeDescription = styled('p')`
  color: ${theme.color.text2};
  font-size: 13px;
  line-height: 1.55;
  margin: ${theme.space.sm} 0 0;
`;

export const NumberInput = styled('input')`
  width: 100%;
  background: ${theme.color.bg};
  color: ${theme.color.text1};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.sm};
  padding: 8px 10px;
  font-family: ${theme.font.mono};
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color ${theme.motion.quick};
  &:focus {
    outline: none;
    border-color: ${theme.color.accent};
    box-shadow: 0 0 0 2px ${theme.color.accentDim};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const ToggleRow = styled('label')`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space.sm};
  margin-top: ${theme.space.lg};
  cursor: pointer;
  user-select: none;
`;

export const ToggleSwitch = styled('span')`
  flex-shrink: 0;
  width: 32px;
  height: 18px;
  border-radius: 999px;
  background: ${(p) => (p.on ? theme.color.accent : theme.color.text6)};
  position: relative;
  transition: background ${theme.motion.base};
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.on ? '16px' : '2px')};
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${theme.color.bg};
    transition: left ${theme.motion.base};
  }
`;

export const ToggleLabel = styled('div')`
  font-size: 13px;
  line-height: 1.4;
  color: ${theme.color.text1};
  & small {
    display: block;
    color: ${theme.color.text3};
    font-size: 11px;
    margin-top: 2px;
  }
`;

export const PrimaryButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space.xs};
  background: ${theme.color.accent};
  color: ${theme.color.accentFg};
  border: none;
  border-radius: ${theme.radius.md};
  padding: 10px 16px;
  font-family: ${theme.font.mono};
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform ${theme.motion.quick}, box-shadow ${theme.motion.quick};
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px ${theme.color.accentGlow};
  }
  &:disabled {
    background: ${theme.color.text6};
    color: ${theme.color.text4};
    cursor: not-allowed;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.color.accentDim};
  }
`;

export const GhostButton = styled('button')`
  background: transparent;
  color: ${theme.color.text2};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.md};
  padding: 8px 14px;
  font-family: ${theme.font.mono};
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all ${theme.motion.quick};
  &:hover:not(:disabled) {
    border-color: ${theme.color.accent};
    color: ${theme.color.accent};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const SlotGrid = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.sm};
`;

export const SlotRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(${(p) => p.cols}, minmax(0, 1fr));
  gap: ${theme.space.sm};
`;

export const SlotRowLabel = styled('div')`
  font-family: ${theme.font.mono};
  font-size: 10px;
  color: ${theme.color.text4};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-top: ${theme.space.xs};
  margin-bottom: 2px;
`;

function slotBorderColor(p) {
  if (p.tone === 'expired') return theme.color.danger;
  if (p.tone === 'expiring') return theme.color.warn;
  if (p.empty) return theme.color.border;
  return theme.color.accent;
}

function pillBackground(tone) {
  if (tone === 'expired') return 'rgba(220, 38, 38, 0.10)';
  if (tone === 'expiring') return 'rgba(217, 119, 6, 0.10)';
  return 'rgba(16, 185, 129, 0.12)';
}

function pillColor(tone) {
  if (tone === 'expired') return theme.color.danger;
  if (tone === 'expiring') return theme.color.warn;
  return theme.color.accent;
}

export const Slot = styled('button')`
  background: ${theme.color.bgRaised};
  border: 1px solid ${slotBorderColor};
  border-radius: ${theme.radius.md};
  padding: ${theme.space.sm};
  min-height: 104px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: ${(p) => (p.empty ? 0.65 : 1)};
  position: relative;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: transform ${theme.motion.quick}, box-shadow ${theme.motion.quick},
    border-color ${theme.motion.quick};
  animation: slotIn 320ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: ${(p) => p.revealDelay || 0}ms;
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadow.card};
    border-color: ${theme.color.accent};
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.color.accentDim};
  }
  @keyframes slotIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: ${(p) => (p.empty ? 0.65 : 1)};
      transform: translateY(0);
    }
  }
`;

export const SlotCoord = styled('div')`
  font-family: ${theme.font.mono};
  font-size: 10px;
  color: ${theme.color.text4};
  letter-spacing: 0.08em;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const OverrideDot = styled('span')`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.color.accent};
  box-shadow: 0 0 0 2px ${theme.color.accentDim};
`;

export const SlotName = styled('div')`
  font-size: 12px;
  color: ${theme.color.text1};
  word-break: break-word;
  line-height: 1.25;
  flex: 1;
`;

export const SlotCount = styled('div')`
  font-family: ${theme.font.mono};
  font-size: 16px;
  font-weight: 600;
  color: ${theme.color.text0};
`;

export const SlotPill = styled('div')`
  font-family: ${theme.font.mono};
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  background: ${(p) => pillBackground(p.tone)};
  color: ${(p) => pillColor(p.tone)};
  align-self: flex-start;
`;

export const SummaryRow = styled('div')`
  display: flex;
  gap: ${theme.space.xl};
  font-family: ${theme.font.mono};
  font-size: 12px;
  color: ${theme.color.text2};
  margin-top: ${theme.space.lg};
  & strong {
    color: ${theme.color.text0};
    font-weight: 600;
  }
`;

export const TelemetryStrip = styled('div')`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  font-family: ${theme.font.mono};
  font-size: 11px;
  color: ${theme.color.text4};
  background: ${theme.color.bgOverlay};
  border-top: 1px solid ${theme.color.border};
  padding: 6px ${theme.space.xl};
  letter-spacing: 0.04em;
  backdrop-filter: blur(8px);
  display: flex;
  gap: ${theme.space.lg};
  white-space: nowrap;
  overflow-x: auto;
  & span strong {
    color: ${theme.color.text2};
  }
`;

export const ModalBackdrop = styled('div')`
  position: fixed;
  inset: 0;
  background: ${theme.color.bgOverlay};
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: backdropIn 180ms ease-out both;
  @keyframes backdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ModalCard = styled('div')`
  background: ${theme.color.bgRaised};
  border: 1px solid ${theme.color.borderStrong};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.modal};
  padding: ${theme.space.xl};
  max-width: 480px;
  width: calc(100% - 40px);
  color: ${theme.color.text1};
`;

export const ModalTitle = styled('h2')`
  font-family: ${theme.font.display};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 ${theme.space.sm};
  color: ${theme.color.text0};
`;

export const ModalBody = styled('p')`
  color: ${theme.color.text2};
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 ${theme.space.lg};
`;

export const ModalActions = styled('div')`
  display: flex;
  gap: ${theme.space.sm};
  justify-content: flex-end;
  margin-top: ${theme.space.lg};
`;

export const ProdBanner = styled('div')`
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid ${theme.color.danger};
  color: ${theme.color.danger};
  border-radius: ${theme.radius.md};
  padding: ${theme.space.sm} ${theme.space.md};
  font-family: ${theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: ${theme.space.md};
`;

export const ProdConfirmLabel = styled('label')`
  display: block;
  font-family: ${theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.color.text3};
  margin: ${theme.space.md} 0 ${theme.space.xs};
  & code {
    background: ${theme.color.text7};
    padding: 1px 6px;
    border-radius: 3px;
    font-family: ${theme.font.mono};
    color: ${theme.color.text1};
  }
`;

export const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    background: theme.color.bg,
    borderColor: state.isFocused ? theme.color.accent : theme.color.border,
    boxShadow: state.isFocused ? `0 0 0 2px ${theme.color.accentDim}` : 'none',
    minHeight: 38,
    fontFamily: theme.font.body,
    '&:hover': { borderColor: theme.color.borderStrong },
  }),
  singleValue: (base) => ({ ...base, color: theme.color.text1 }),
  placeholder: (base) => ({ ...base, color: theme.color.text4 }),
  menu: (base) => ({
    ...base,
    background: theme.color.bgRaised,
    border: `1px solid ${theme.color.border}`,
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? theme.color.border : 'transparent',
    color: theme.color.text1,
    fontFamily: theme.font.body,
    cursor: 'pointer',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, color: theme.color.text1 }),
};
